import { RETENTION } from "@bridewell/shared";
import { createFileRoute, Link } from "@tanstack/react-router";
import { SEO } from "@/features/marketing/seo";
import { LegalTable, LegalTitle, Operator } from "@/components/legal/legal-layout";
import { LEGAL } from "@/legal/config";

/**
 * The page Google Play links as the app's "delete account" and "delete data" URL: it must name the app, put the steps
 * first and say what is deleted, what is kept and for how long. Every period is a RETENTION constant, as in the privacy
 * policy (§7), which this page must never contradict.
 */
export const Route = createFileRoute("/legal/delete-account")({ head: SEO.deleteAccount, component: DeleteAccount });

const DAYS = RETENTION.softDeleteDays;
const NAME = LEGAL.serviceName;
const EMAIL = LEGAL.operator.privacyEmail;

function DeleteAccount() {
  return (
    <>
      <LegalTitle title={`Delete your ${NAME} account or data`} updated={LEGAL.deleteAccountUpdated} />

      <p>
        This page explains how to delete your {NAME} account, or only some of your data, and what happens to each kind of
        data. It applies to the {NAME} website ({LEGAL.appHost}) and the {NAME} app for Android, which share one account.
        The {NAME} apps for iPhone, iPad, Mac and Apple Vision Pro keep their data in your own iCloud and do not use a{" "}
        {NAME} account.
      </p>

      <h2>1. Delete your account</h2>
      <ul>
        <li><strong>In the Android app:</strong> More → Account → <strong>Delete account</strong>, enter your password, then <strong>Delete my account</strong>.</li>
        <li><strong>On the web:</strong> sign in at <a href={`https://${LEGAL.appHost}/app/account`}>{LEGAL.appHost}</a>, open Account → <strong>Delete account</strong> and confirm with your password.</li>
        <li><strong>If you cannot sign in:</strong> email <a href={`mailto:${EMAIL}?subject=${encodeURIComponent(`Delete my ${NAME} account`)}`}>{EMAIL}</a> from the address of your account and ask us to delete it. We answer within one month, as the GDPR requires.</li>
      </ul>
      <p>
        Deleting your account deletes every wedding you own, for everyone in it, and cannot be undone. Weddings you joined
        stay with their owners; only your access to them is removed. A running subscription stops: one bought on the web
        is cancelled at once, one bought in {LEGAL.googlePlay.short} no longer renews. To keep a copy first, use Account →
        <strong> Download my data</strong>.
      </p>

      <h2>2. Delete some of your data and keep your account</h2>
      <ul>
        <li><strong>One entry</strong> (a guest, task, budget line, vendor, note, photo or any other item): open it and choose <strong>Delete</strong>.</li>
        <li><strong>A whole wedding you own:</strong> the wedding's Settings → <strong>Delete wedding</strong> (in the Android app: More → Settings). It is deleted for everyone in it.</li>
        <li><strong>A wedding you joined:</strong> the wedding's Settings → <strong>Leave wedding</strong>.</li>
        <li><strong>Anything you cannot delete yourself:</strong> email <a href={`mailto:${EMAIL}`}>{EMAIL}</a> and say what you want deleted. We answer within one month.</li>
      </ul>

      <h2>3. What is deleted, what is kept, and for how long</h2>
      <LegalTable
        head={["Data", "What happens"]}
        rows={[
          ["Your name, email address, password and sign-in sessions (with their IP address and device)", "Deleted as soon as you delete your account."],
          ["Weddings you own and everything in them: guests, budget, checklist, vendors, notes, photos, contracts", `Deleted with your account and permanently erased, files included, within ${DAYS} days at the latest.`],
          ["Your access to weddings you joined", "Removed as soon as you delete your account. The weddings themselves belong to their owners."],
          ["An entry or wedding you delete while keeping your account", `Kept for a ${DAYS}-day grace period, during which we can restore it if you ask, then permanently erased, files included.`],
          ["Your plan, purchase history, templates and reminder settings", "Deleted with your account."],
          ["Payment notifications from Paddle and Google Play", `Kept for up to ${RETENTION.billingEventDays} days for support and disputes, then deleted. They contain no card details. ${LEGAL.merchantOfRecord.short} and Google, as the sellers, keep their own transaction records for as long as tax law requires.`],
          ["Error logs and the app's error reports", `Deleted after ${RETENTION.serverLogDays} days at the latest.`],
          ["The log of reminder emails sent (date and kind, never the content)", `Deleted after ${RETENTION.notificationLogDays} days at the latest.`],
          ["Messages you sent through the contact form", `Kept in our mailbox until the enquiry is closed, at most ${RETENTION.contactEnquiryMonths} months.`],
          ["Database backups", `Roll off within ${RETENTION.backupDays} days, so deleted data leaves the backups within that time too.`],
        ]}
      />
      <p>
        More detail is in the <Link to="/legal/privacy">Privacy Policy</Link>. Questions about your data:{" "}
        <a href={`mailto:${EMAIL}`}>{EMAIL}</a>.
      </p>

      <h2>4. Who is responsible</h2>
      <Operator />
    </>
  );
}
